// src/Pages/Usuario/Agenda/hooks/useAgendaForm.js

import { useEffect, useState } from "react";
import { auth, db } from "../../../../firebase/config";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

function parseDisplayName(displayName) {
  if (!displayName) return { nombres: "", apellidos: "" };

  const partes = displayName.trim().split(/\s+/);

  if (partes.length === 1) {
    return { nombres: partes[0], apellidos: "" };
  }

  if (partes.length === 2) {
    return { nombres: partes[0], apellidos: partes[1] };
  }

  // 2 nombres + resto apellidos (criterio usado en EKINE)
  return {
    nombres: partes.slice(0, 2).join(" "),
    apellidos: partes.slice(2).join(" "),
  };
}


export default function useAgendaForm(usuario, regionesYComunas) {
  // ========================
  // 1) Estado unificado del formulario
  // ========================
  const [form, setForm] = useState({
    nombres: "",
    apellidos: "",
    rut: "",
    correo: "",
    celular: "+569",

    t_emergencia: "+569",

    password: "",
    confirmPassword: "",

    servicio: null,
    variante: null,
    fecha: "",
  });

  const [comunasFiltradas, setComunasFiltradas] = useState([]);
  const [mostrarDatos, setMostrarDatos] = useState(true);

  // ========================
  // 2) handleChange genérico
  // ========================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // ========================
  // 3) Cargar datos Firestore → form
  // ========================
  useEffect(() => {
    if (!usuario) return;

    const cargar = async () => {
      const ref = doc(db, "usuarios", usuario.email.toLowerCase());
      const snap = await getDoc(ref);
      if (!snap.exists()) return;

      const d = snap.data();

      setForm((f) => ({
        ...f,
        nombres: d.nombres || "",
        apellidos: d.apellidos || "",
        rut: d.rut || "",

        correo: d.correo || usuario.email.toLowerCase(),
        celular: d.celular || "+569",

        t_emergencia: d.t_emergencia|| "+569",
      }));

      setMostrarDatos(false); // si ya tiene datos → colapsado
    };

    cargar();
  }, [usuario]);


  // ========================
  // 5) Crear cuenta (registro dentro de agenda)
  // ========================
const crearCuenta = async () => {
  if (
    !form.nombres ||
    !form.apellidos ||
    !form.rut ||
    !form.correo ||
    !form.celular
  ) {
    return alert("Completa todos los campos obligatorios marcados con *.");
  }

  if (!form.password || !form.confirmPassword)
    return alert("Debe completar ambas contraseñas.");

  if (form.password !== form.confirmPassword)
    return alert("❌ Las contraseñas no coinciden.");

  const email = form.correo.toLowerCase().trim();

  try {
    // ✅ 1) Crear cuenta en AUTH (esto funciona sin estar logueado)
    const cred = await createUserWithEmailAndPassword(auth, email, form.password);

    // ✅ 2) Ya logueado -> recién ahora crear/mergear el doc usuarios/{email}
    const ref = doc(db, "usuarios", email);

    await setDoc(
      ref,
      {
        uid: cred.user.uid,
        correo: email,

        nombres: form.nombres.trim(),
        apellidos: form.apellidos.trim(),
        rut: form.rut.trim(),

        celular: (form.celular || "").trim(),
        t_emergencia: (form.t_emergencia || "").trim(),

        primerasSesiones: {
          entrenamiento: false,
          kinesiologia: false,
          masajes: false,
          recovery: false,
        },

        origen: "agenda_registro",
        creadoEn: serverTimestamp(),
        actualizadoEn: serverTimestamp(),
      },
      { merge: true }
    );

    alert("✅ Cuenta creada con éxito, continúa con tu reserva.");
  } catch (err) {
    console.error(err);

    // ✅ si ya existe en Auth
    if (err?.code === "auth/email-already-in-use") {
      alert("Ya existe una cuenta con este correo. Inicia sesión.");
      return;
    }

    // otros errores comunes
    if (err?.code === "auth/invalid-email") {
      alert("Correo inválido.");
      return;
    }
    if (err?.code === "auth/weak-password") {
      alert("La contraseña debe tener al menos 6 caracteres.");
      return;
    }

    alert("Error creando la cuenta.");
  }
};


  // ========================
  // 6) Registro con Google
  // ========================
  const provider = new GoogleAuthProvider();

  const handleGoogleSignup = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const email = user.email.toLowerCase();

      const ref = doc(db, "usuarios", email);
      const snap = await getDoc(ref);

      const { nombres, apellidos } = parseDisplayName(user.displayName);

      if (!snap.exists()) {

        await setDoc(ref, {
          uid: user.uid,
          correo: email,
          nombres,
          apellidos,
          rut: "",
          celular: "",
          t_emergencia: "",
          origen: "agenda_google",
          primerasSesiones: {
            entrenamiento: false,
            kinesiologia: false,
            masajes: false,
            recovery: false,
          },
          creadoEn: serverTimestamp(),
        });
      }
    } catch (err) {
      console.error(err);
      alert("Error con Google Sign-in.");
    }
  };

  // ========================
  // 7) Validación global
  // ========================
  const camposBasicos = [
    "nombres",
    "apellidos",
    "rut",
    "correo",
    "celular",
  ];

  const faltantes = camposBasicos.filter((c) => !form[c]);

  return {
    form,
    setForm,
    comunasFiltradas,
    mostrarDatos,
    setMostrarDatos,
    handleChange,
    crearCuenta,
    handleGoogleSignup,
    faltantes,
  };
}
